import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  BaseUploadRow,
  ComposeCampaignRequest,
  DedicatedCliRow,
  IvrFlowRow,
  PromptUploadRow,
  TtsFlowRow,
  TtsTypeRow,
  UpdateCampaignRequest,
  UserCliRow,
  UserGroupAgentRow,
  UserLanguageRow,
  UserLocationRow,
  UserModuleRow,
} from './types';

export const voiceComposeKeys = {
  all: ['voice-campaigns'] as const,
  base: (uid: string | number) => ['voice-campaigns', 'base', uid] as const,
  prompts: (uid: string | number) => ['voice-campaigns', 'prompts', uid] as const,
  promptsAdmin: (uid: string | number) => ['voice-campaigns', 'prompts-admin', uid] as const,
  userLocs: (uid: string | number) => ['voice-campaigns', 'user-locs', uid] as const,
  cliUser: (uid: string | number) => ['voice-campaigns', 'cli-user', uid] as const,
  dedicatedCli: (uid: string | number) => ['voice-campaigns', 'dedicated-cli', uid] as const,
  ivr: (uid: string | number) => ['voice-campaigns', 'ivr', uid] as const,
  ttsFlow: (uid: string | number) => ['voice-campaigns', 'tts-flow', uid] as const,
  ttsTypes: ['voice-campaigns', 'tts-types'] as const,
  languages: (uid: string | number) => ['voice-campaigns', 'languages', uid] as const,
  groupAgents: (uid: string | number) => ['voice-campaigns', 'group-agents', uid] as const,
  modules: (uid: string | number) => ['voice-campaigns', 'modules', uid] as const,
  clone: (id: string | number) => ['voice-campaigns', 'clone', id] as const,
};

function useUid() {
  return useAuthStore((s) => s.userId);
}

/* ---------------- Lookups ---------------- */

export function useBaseUploads() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.base(uid ?? 'none'),
    queryFn: () => api.get<BaseUploadRow[]>(`/api/obd/base/${uid}`),
    enabled: Boolean(uid),
    staleTime: 60_000,
    select: (d) => ensureArray<BaseUploadRow>(d),
  });
}

export function usePromptUploads(opts?: { includeTtsFlow?: boolean; enabled?: boolean }) {
  const uid = useUid();
  const inc = opts?.includeTtsFlow === true;
  return useQuery({
    queryKey: inc
      ? [...voiceComposeKeys.prompts(uid ?? 'none'), 'tts-flow']
      : voiceComposeKeys.prompts(uid ?? 'none'),
    queryFn: () =>
      api.get<PromptUploadRow[]>(`/api/obd/prompts/list/${uid}${inc ? '?includeTtsFlow=true' : ''}`),
    enabled: Boolean(uid) && opts?.enabled !== false,
    staleTime: 60_000,
    select: (d) => ensureArray<PromptUploadRow>(d),
  });
}

export function usePromptSadminUploads(opts?: { includeTtsFlow?: boolean; enabled?: boolean }) {
  const uid = useUid();
  const inc = opts?.includeTtsFlow === true;
  return useQuery({
    queryKey: inc
      ? [...voiceComposeKeys.promptsAdmin(uid ?? 'none'), 'tts-flow']
      : voiceComposeKeys.promptsAdmin(uid ?? 'none'),
    queryFn: () =>
      api.get<PromptUploadRow[]>(`/api/obd/prompts/list${inc ? '?includeTtsFlow=true' : ''}`),
    enabled: Boolean(uid) && opts?.enabled !== false,
    staleTime: 60_000,
    select: (d) => ensureArray<PromptUploadRow>(d),
  });
}

export function useUserLocations() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.userLocs(uid ?? 'none'),
    queryFn: () => api.get<UserLocationRow[]>(`/api/obd/user/locations/${uid}`),
    enabled: Boolean(uid),
    staleTime: 60_000,
    select: (d) => ensureArray<UserLocationRow>(d),
  });
}

export function useUserClis() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.cliUser(uid ?? 'none'),
    queryFn: () => api.get<UserCliRow[]>(`/api/obd/cli/user/${uid}`),
    enabled: Boolean(uid),
    staleTime: 60_000,
    select: (d) => ensureArray<UserCliRow>(d),
  });
}

export function useAllocatedDedicatedClis() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.dedicatedCli(uid ?? 'none'),
    queryFn: () => api.get<DedicatedCliRow[]>(`/api/obd/dedicated/cli/allocated/${uid}`),
    enabled: Boolean(uid),
    staleTime: 60_000,
    select: (d) => ensureArray<DedicatedCliRow>(d),
  });
}

export function useIvrFlows() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.ivr(uid ?? 'none'),
    queryFn: () => api.get<IvrFlowRow[]>(`/api/obd/custom/ivr/list/${uid}`),
    enabled: Boolean(uid),
    staleTime: 60_000,
    select: (d) => ensureArray<IvrFlowRow>(d),
  });
}

export function useTtsFlows() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.ttsFlow(uid ?? 'none'),
    queryFn: () => api.get<TtsFlowRow[]>(`/api/obd/tts/flow/list/${uid}`),
    enabled: Boolean(uid),
    staleTime: 60_000,
    select: (d) => ensureArray<TtsFlowRow>(d),
  });
}

/** GET /api/obd/tts/type/list — global, not per-user. */
export function useTtsTypes() {
  return useQuery({
    queryKey: voiceComposeKeys.ttsTypes,
    queryFn: () => api.get<TtsTypeRow[]>('/api/obd/tts/type/list'),
    staleTime: 5 * 60_000,
    select: (d) => ensureArray<TtsTypeRow>(d),
  });
}

export function useUserLanguages() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.languages(uid ?? 'none'),
    queryFn: () => api.get<UserLanguageRow[]>(`/api/obd/user/languages/${uid}`),
    enabled: Boolean(uid),
    staleTime: 60_000,
    select: (d) => ensureArray<UserLanguageRow>(d),
  });
}

export function useUserGroupAgents() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.groupAgents(uid ?? 'none'),
    queryFn: () => api.get<UserGroupAgentRow[]>(`/api/obd/group/agent/list/${uid}`),
    enabled: Boolean(uid),
    staleTime: 60_000,
    select: (d) => ensureArray<UserGroupAgentRow>(d),
  });
}

export function useUserModules() {
  const uid = useUid();
  return useQuery({
    queryKey: voiceComposeKeys.modules(uid ?? 'none'),
    queryFn: () => api.get<UserModuleRow[]>(`/api/obd/module/list/${uid}`),
    enabled: Boolean(uid),
    staleTime: 5 * 60_000,
    select: (d) => ensureArray<UserModuleRow>(d),
  });
}

/** Used by Update / Clone to pre-populate the wizard. */
export function useCampaignClone(campaignId: string | number | null) {
  return useQuery({
    queryKey: voiceComposeKeys.clone(campaignId ?? 'none'),
    queryFn: () => api.get<unknown>(`/api/obd/campaign/clone/${campaignId}`),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
    retry: false,
  });
}

/* ---------------- Uploads ---------------- */

export interface UploadContactListPayload {
  userId: number;
  templateId: string;
  contactList: string;
}

export interface UploadBaseFilePayload {
  userId: number;
  templateId: string;
  baseFile: File;
  baseName: string;
  contactList?: string;
}

export interface UploadResponse {
  baseId?: number;
  message?: string;
  skippedCount?: number;
}

export function useUploadContactList() {
  return useMutation({
    mutationFn: (payload: UploadContactListPayload) => {
      const fd = new FormData();
      fd.append('userId', String(payload.userId));
      fd.append('contactList', payload.contactList);
      fd.append('templateId', payload.templateId);
      return apiClient
        .post<UploadResponse>('/api/obd/contactlist', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
  });
}

export function useUploadBaseFile() {
  return useMutation({
    mutationFn: (payload: UploadBaseFilePayload) => {
      const fd = new FormData();
      fd.append('baseFile', payload.baseFile);
      fd.append('baseName', payload.baseName);
      fd.append('userId', String(payload.userId));
      fd.append('contactList', payload.contactList ?? '');
      fd.append('templateId', payload.templateId);
      return apiClient
        .post<UploadResponse>('/api/obd/baseupload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
  });
}

/* ---------------- Compose / Update ---------------- */

function useInvalidateCampaigns() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['campaigns'] });
    qc.invalidateQueries({ queryKey: voiceComposeKeys.all });
  };
}

export function useComposeCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (req: ComposeCampaignRequest) =>
      api.post<{ message?: string; status?: string }>('/api/obd/campaign/compose', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (req: UpdateCampaignRequest) =>
      api.post<{ message?: string; status?: string }>('/api/obd/campaign/compose/edit', req),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/campaign/compose/outdialV2 — continuous campaigns (template 10). */
export function useComposeCampaignV2() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (body: FormData) =>
      apiClient
        .post<{ message?: string; status?: string }>('/api/obd/campaign/compose/outdialV2', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/campaign/compose/update/continuous — continuous campaigns (template 10). */
export function useComposeUpdateContinuousCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (body: FormData) =>
      apiClient
        .post<{ message?: string; status?: string }>(
          '/api/obd/campaign/compose/update/continuous',
          body,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        )
        .then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}
